// src/pages/OwnerProfile.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom"; // ✅ FIX: import Link
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useUser } from "../context/UserContext";
import { fetchOwnerProfile } from "../api/ownerPortalApi";

export default function OwnerProfile() {
  const { isOwner, isAuthenticated } = useUser();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const loadProfile = async (isRefresh = false) => {
    try {
      if (!isRefresh) setLoading(true);
      setError("");
      const data = await fetchOwnerProfile();
      setProfile(data || null);
    } catch (err) {
      console.error("Failed to fetch owner profile", err);
      setError("Could not load owner profile. Please try again.");
    } finally {
      if (!isRefresh) setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && isOwner) {
      loadProfile(false);
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, isOwner]);

  if (!isAuthenticated || !isOwner) {
    return (
      <div className="min-h-screen bg-white text-slate-900">
        <Header />
        <main className="mx-auto max-w-xl px-4 sm:px-6 xl:px-8 py-16">
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-8 text-sm text-slate-700">
            Please sign in as a vehicle owner to view your owner profile.
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const name = profile?.name || "—";
  const phone = profile?.phoneNumber || "—";
  const email = profile?.email || "—";
  const address = profile?.address || "—";

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Header />
      <main className="mx-auto max-w-2xl px-4 sm:px-6 xl:px-8 py-12">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold">Owner profile</h1>
              <p className="mt-1 text-sm text-slate-600">
                View your registered owner details for Wayroot.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setRefreshing(true);
                loadProfile(true);
              }}
              disabled={refreshing || loading}
              className="inline-flex items-center rounded-xl border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {refreshing || loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          {error && (
            <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700">
              {error}
            </div>
          )}

          {loading && !profile ? (
            <div className="mt-8 text-sm text-slate-500">Loading profile…</div>
          ) : (
            <div className="mt-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-slate-100 border border-slate-300 flex items-center justify-center text-sm font-semibold text-slate-700">
                  {String(name || phone || "O").trim().charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    {name}
                  </div>
                  <div className="text-xs text-slate-500">
                    Owner account (Wayroot partner)
                  </div>
                </div>
              </div>

              <div className="mt-4 border-t border-slate-200 pt-4 space-y-3 text-sm">
                <Row label="Owner name" value={name} />
                <Row label="Phone number" value={phone} />
                <Row label="Email" value={email} />
                <Row label="Address" value={address} multiline />
              </div>

              <p className="mt-4 text-xs text-slate-500">
                To update these details, please use your owner onboarding or
                contact support. Password for this owner login can be managed
                from{" "}
                <Link
                  to="/owner/security"
                  className="font-medium text-[color:var(--brand-pink,#FF2D83)] hover:underline"
                >
                  Set / change password
                </Link>
                .
              </p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Row({ label, value, multiline = false }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div
        className={`mt-0.5 text-sm text-slate-900 ${
          multiline ? "whitespace-pre-line" : ""
        }`}
      >
        {value || "—"}
      </div>
    </div>
  );
}
